
























import Vue from "vue";

import IconCorrect from "../../assets/icons/Correct.svg";
import IconIncorrect from "../../assets/icons/Incorrect.svg";
import IconNeutralSelected from "../../assets/icons/misc/TT4WRadioChecked.svg";
import IconUnselected from "../../assets/icons/misc/TT4WRadio.svg";
import { MCQOptions } from "../../models/drill";

import JzIcon from "../image/JzIcon.vue";
import JzList from "../layout/JzList.vue";
import JzListItem from "../layout/JzListItem.vue";

/**
 * List of all available icons key for convenience only. Do not use this type
 * for any type-checking.
 */
type IconKey = "correct" | "incorrect" | "selected" | "unselected";

interface Methods {
  getCssClasses: (option: MCQOptions) => string;
  getIcon: (option: MCQOptions) => string | undefined;
  onOptionSelect: (option: MCQOptions) => void;
}

interface Computed {
  canSelectOption: boolean;
}

interface Props {
  // If true, then options are always in bold fonts
  boldOptions: boolean;
  // Allow re-selection upon first selection. Usually paired with
  // `showAnswer: false`.
  canReselect: boolean;
  // Selection is allowed, if not disabled.
  canSelect: boolean;
  // If defined, question has been answered and selection cannot be
  // done / changed.
  correctAnswerId: string | number | undefined;
  // If true, selections are not allowed and text color is changed.
  disabled: boolean;
  // List of icons set. This comes useful when a custom set of icons
  // is required (e.g. custom correct / incorrect or custom selected /
  // unselected icons). Note that partial definition is not allowed:
  // all icons must be declared at once.
  icons: { [key in IconKey]: string };
  // List of provided options. ID must be defined and unique.
  options: MCQOptions[];
  // If true and if `correctAnswerId` is defined, display correct
  // and incorrect option
  showAnswer: boolean;
  // If true, unselected options display an empty radio icon
  showRadioIcon: boolean;
  // v-model input, referring to selected option ID
  value: string | number | undefined;
}

/**
 * v-model is a number or string reflecting the selected option ID.
 *
 * @emits input argument is the selected option ID
 * @emits submit argument is the selected option
 *
 * -- Note --
 * The `&&` syntax is used on the `@click` event listener because if option
 * cannot be selected, `@click` must resolve to null or undefined to avoid
 * triggering onHover styling.
 * Upon answered / disabled / read-only question, selection must not be
 * possible so hovering each options must not trigger background-color change.
 *
 * -- Note --
 * Reference for scoped slots: https://codepen.io/Kradek/pen/RpveGy
 */
export default Vue.extend<{}, Methods, Computed, Props>({
  name: "JzMultipleChoiceQuestion",
  components: { JzIcon, JzList, JzListItem },

  props: {
    boldOptions: { type: Boolean, default: false },
    canReselect: { type: Boolean, default: false },
    canSelect: { type: Boolean, default: true },
    correctAnswerId: { type: [String, Number], default: undefined },
    disabled: { type: Boolean, default: false },
    icons: {
      type: Object as () => { [key in IconKey]: string },
      default: () => ({
        correct: IconCorrect,
        incorrect: IconIncorrect,
        selected: IconNeutralSelected,
        unselected: IconUnselected
      })
    },
    options: { type: Array as () => MCQOptions[], required: true },
    showAnswer: { type: Boolean, default: true },
    showRadioIcon: { type: Boolean, default: false },
    value: { type: [String, Number], default: "" }
  },

  data: function() {
    return {};
  },

  computed: {
    canSelectOption() {
      // Selection is allowed and question is not disabled
      const canSelectFromProps = this.canSelect && !this.disabled;
      // Question allows re-selection or answer has not been submitted yet
      const canSelectFromSelection = this.canReselect || this.value === "";

      return canSelectFromProps && canSelectFromSelection;
    }
  },

  methods: {
    getCssClasses(option) {
      let cssClasses: string[] = [];

      // @todo: better workaround to handle the different padding?
      if (option.description) {
        cssClasses = [...cssClasses, "has-description"];
      }

      // Forward disabled status to options
      if (this.disabled) {
        cssClasses = [...cssClasses, "is-disabled"];
      }

      // Display selection
      if (this.value === option.id) {
        cssClasses = [...cssClasses, "is-selected"];
      }

      // Display if option is correct or incorrect
      if (this.showAnswer) {
        if (option.id === this.correctAnswerId) {
          cssClasses = [...cssClasses, "is-correct"];
        } else if (this.value === option.id) {
          cssClasses = [...cssClasses, "is-incorrect"];
        }
      }

      // Define if the options are not selectable
      if (!this.canSelectOption) {
        cssClasses = [...cssClasses, "is-unselectable"];
      }

      return cssClasses.join(" ");
    },

    getIcon(option) {
      // Correctness is not displayed
      if (!this.showAnswer) {
        if (option.id === this.value) {
          return this.icons.selected;
        }
      }
      // Correctness is displayed
      else {
        // Option is the correct one, regardless if it is selected or not
        if (option.id === this.correctAnswerId) {
          return this.icons.correct;
        }

        // Option is selected but cannot be the correct one
        if (option.id === this.value) {
          return this.icons.incorrect;
        }
      }

      // Option is neither selected, neither correct: show the unselected icon
      // if applicable
      if (this.showRadioIcon) {
        return this.icons.unselected;
      }

      // Return empty string instead of undefined to make the slot props reactive
      return "";
    },

    onOptionSelect(option) {
      this.$emit("input", option);
      this.$emit("submit", option);
    }
  }
});
